import { FormItem } from '@framework/form/item/FormItem.tsx';
import {
  InfoPopoverContentWrapper,
  InfoPopoverText,
} from '@framework/ui/components/popover/InfoPopoverContent.tsx';
import { Collapse, Space } from 'antd';
import { useWatch } from 'react-hook-form';
import { useTreeReferencesContext } from '../../../../tree/references/TreeReferencesContext.tsx';
import { StyledDrawerCollapse } from '../../../../ui/drawer/StyledDrawerCollapse.tsx';
import { SimilInputSpan } from '../../../../ui/form/SimilInputSpan.tsx';
import {
  type FieldDatatype,
  isObjectsArrayField,
  isSameFieldDatatype,
} from '../../../expressions/datatype/Datatype.ts';
import { isExpressionReferenceOfObjectsArray } from '../../../expressions/datatype/isExpressionReferenceOfObjectsArray.ts';
import { type IExpression } from '../../../expressions/IExpression.ts';
import { ExpressionReferenceInput } from '../../../expressions/references/input/ExpressionReferenceInput.tsx';
import {
  AssignableExpressionInput,
  type IAssignableExpressionAllowsReference,
} from '../../../expressions/repository/assignable/input/AssignableExpressionInput.tsx';
import { WidgetRepository } from '../WidgetRepository.ts';

const { Panel } = Collapse;

/** Popover informativo sul valore di default del campo. */
const WidgetDefaultValueInfoPopover = (
  <InfoPopoverContentWrapper>
    <InfoPopoverText>
      L&apos;icona a lato del campo seleziona la modalità: <br />
      <b>Valore</b> (puntina) un valore fisso; <br />
      <b>Riferimento</b> (link) valore copiato da un altro campo;
      <br />
      <b>Funzione</b> (fx) valore calcolato tramite una funzione.
    </InfoPopoverText>
  </InfoPopoverContentWrapper>
);

export interface IDefaultValueInputProps {
  /**
   * Permette di personalizzare il filtro sulle reference selezionabili.
   */
  allowsReferences?: IAssignableExpressionAllowsReference;
  /**
   * Se fornito, permette di controllare il fieldDataType da utilizzare.
   * Altrimenti, viene estratto automaticamente dal widget type.
   */
  forceFieldDataType?: FieldDatatype;
}

/**
 * Field del valore di default del widget corrente; undefined se il widget
 * non espone una reference. Usato come guardia prima di montare il fill
 * della card "Compilazione".
 */
export function useDefaultValueField(forceFieldDataType?: FieldDatatype) {
  const { widget } = useTreeReferencesContext();
  return (
    forceFieldDataType ??
    WidgetRepository.find(widget?.type)?.getReference?.([], widget!)?.field
  );
}

export function DefaultValueInput(props: IDefaultValueInputProps) {
  const { references, widget } = useTreeReferencesContext();
  const field = useDefaultValueField(props.forceFieldDataType);

  const childReferences =
    field && isObjectsArrayField(field) ? field.structure : [];

  const defaultValue: IExpression | null = useWatch({ name: 'defaultValue' });

  if (!field) return null;

  return (
    <>
      <FormItem
        name="defaultValue"
        label="Valore di default"
        popover={{
          title: 'Valore di default',
          content: WidgetDefaultValueInfoPopover,
        }}
        help={
          <>
            Il <b>Valore di default</b> precompila il campo alla prima apertura
            del modulo; resta modificabile da chi compila.
          </>
        }
      >
        <AssignableExpressionInput
          size="large"
          block
          // Rimuovo la reference a sé stesso (non ha senso avere un valore di default che fa riferimento a se stesso)
          references={references.filter(r => r.id !== widget?.id)}
          name="defaultValue"
          field={field}
          allowsReference={props.allowsReferences}
        />
      </FormItem>

      {field && isExpressionReferenceOfObjectsArray(defaultValue) && (
        <StyledDrawerCollapse defaultActiveKey={['list-default']} ghost>
          <Panel
            header="Valori di default per gli elementi della lista"
            key="list-default"
          >
            {defaultValue.value.field.structure.map((child, index) => (
              <div key={`${child.id}`} style={{ marginBottom: 8 }}>
                <FormItem
                  key={`${child.id}`}
                  name={`defaultValueChildren.${index}`}
                  layout={{ removeMargin: true }}
                >
                  <Space.Compact block>
                    <SimilInputSpan>{child.label}</SimilInputSpan>
                    <ExpressionReferenceInput
                      width="100%"
                      allowClear
                      name={`defaultValueChildren.${index}`}
                      references={childReferences.filter(r =>
                        isSameFieldDatatype(child.field, r.field),
                      )}
                    />
                  </Space.Compact>
                </FormItem>
              </div>
            ))}
          </Panel>
        </StyledDrawerCollapse>
      )}
    </>
  );
}
